import { Component, ErrorInfo, ReactNode } from 'react';
import { captureError } from '@/lib/monitoring';

/**
 * Catches React render crashes so a broken component shows a recovery card
 * instead of a white screen — and reports the crash to Sentry via the
 * observability endpoint before rendering the fallback.
 */

interface Props {
  children: ReactNode;
  area?: string;
}

interface State {
  hasError: boolean;
  message: string;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error?.message || 'Unexpected error' };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureError({
      message: error?.message || 'React render crash',
      name: error?.name || 'ReactError',
      stack: `${error?.stack || ''}\n--- component stack ---${info?.componentStack || ''}`.slice(0, 6000),
      level: 'fatal',
      handled: false,
      area: this.props.area || 'react-render',
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4 py-16">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <svg viewBox="0 0 24 24" className="h-6 w-6 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-slate-900">Something went wrong on this page</h2>
          <p className="mt-2 text-sm text-slate-600">
            The problem has been reported automatically. Your account, credits and purchases are unaffected.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Reload page
            </button>
            <a
              href="/"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
